import Drawer from '@material-ui/core/Drawer';
import {Button} from '@wandb/weave/components';
import {TextField} from '@wandb/weave/components/Form/TextField';
import React, {FC, useContext, useState} from 'react';
import {toast} from 'react-toastify';

import {envIsPublicCloud} from '../../config';
import {
  useCreateOAuthClientMutation,
  useCreateServiceAccountMutation,
  useOauthProvidersQuery,
  useTeamOrganizationQuery,
} from '../../generated/graphql';
import * as C from '../../pages/HomePage/HomePageSidebar/CommonDrawer.styles';
import * as S from '../../pages/HomePage/HomePageSidebar/CreateProjectDrawer.styles';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../../pages/OrgDashboard/OrgRoles/RolesDashboard';
import {extractErrorMessageFromApolloError} from '../../util/errors';
import {ResizableDrawer} from '../Launch/ResizableDrawer';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {ServiceAccountAuthOptions} from './ServiceAccountAuthOptions';
import {ServiceAccountAuthMethod} from './ServiceAccountAuthPill';

type ServiceAccountDrawerProps = {
  isOpen: boolean;
  close: () => void;
  entityName: string;
  refetch: () => void;
};

export const ServiceAccountDrawer: FC<ServiceAccountDrawerProps> = ({
  isOpen,
  close,
  entityName,
  refetch,
}) => {
  const {data: organizationData} = useTeamOrganizationQuery({
    variables: {entityName},
  });

  const organization = organizationData?.entity?.organization;

  const [serviceAccountName, setServiceAccountName] = useState<string>('');
  const [authMethod, setAuthMethod] = useState<ServiceAccountAuthMethod>(
    ServiceAccountAuthMethod.API_KEY
  );
  const [subject, setSubject] = useState<string>('');

  const [createServiceAccountMutation, {loading}] =
    useCreateServiceAccountMutation();

  const [createOAuthClientMutation, {loading: clientLoading}] =
    useCreateOAuthClientMutation();

  const {selectedAccount} = useContext(AccountSelectorContext);
  const accountName = selectedAccount?.name || '';
  const organizationName = envIsPublicCloud
    ? accountName
    : DEFAULT_ONPREM_ORGANIZATION_NAME;

  const {data} = useOauthProvidersQuery({
    variables: {
      organizationName,
    },
  });
  const providers = data?.organization?.OAuthProviders ?? [];

  const subjectRequired = authMethod === ServiceAccountAuthMethod.FEDERATED;
  const isCreateDisabled =
    !serviceAccountName || !authMethod || (subjectRequired && !subject);

  const createServiceAccount = async () => {
    try {
      await createServiceAccountMutation({
        variables: {
          entityName,
          description: serviceAccountName,
          organizationId: organization?.id,
        },
      });

      refetch();
      toast.success('Service account created');
      setServiceAccountName('');
      close();
    } catch (err) {
      toast(extractErrorMessageFromApolloError(err));
    }
  };

  const createOAuthClient = async () => {
    if (providers.length === 0) {
      toast.error('No identity provider configured');
    }
    try {
      await createOAuthClientMutation({
        variables: {
          entityName,
          name: serviceAccountName,
          subject,
          providerId: providers[0].id, // organizations can only have 1 provider for now
        },
      });

      refetch();
      toast.success('Service account created');
      setServiceAccountName('');
      setSubject('');
      close();
    } catch (err) {
      toast(extractErrorMessageFromApolloError(err));
    }
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={close}>
      <ResizableDrawer startWidth={500} minWidth={400}>
        <C.DrawerContents>
          <C.TopPanel>
            <C.HeaderRow>
              <C.Header>Create a new service account</C.Header>
              <C.Close>
                <Button
                  onClick={close}
                  icon="close"
                  size="large"
                  variant="ghost"
                />
              </C.Close>
            </C.HeaderRow>
            <C.UnderLine />
            <S.FormRow>
              <C.Label>Name</C.Label>
              <TextField
                value={serviceAccountName}
                dataTest="service-account-name-input"
                onChange={setServiceAccountName}
              />
            </S.FormRow>
            <S.FormRow>
              <C.Label>Authentication Method</C.Label>
              <ServiceAccountAuthOptions
                selectedAuthMethod={authMethod}
                setSelectedAuthMethod={setAuthMethod}
                isFederationEnabled={providers.length > 0}
              />
            </S.FormRow>
            {authMethod === ServiceAccountAuthMethod.FEDERATED && (
              <S.FormRow>
                <C.Label>Subject</C.Label>
                <TextField value={subject} onChange={setSubject} />
              </S.FormRow>
            )}
          </C.TopPanel>
          <Button
            disabled={isCreateDisabled || loading}
            data-test="create-service-account-button"
            className="w-full"
            variant="primary"
            size="large"
            onClick={
              authMethod === ServiceAccountAuthMethod.API_KEY
                ? createServiceAccount
                : createOAuthClient
            }>
            Create
          </Button>
        </C.DrawerContents>
      </ResizableDrawer>
    </Drawer>
  );
};
